import { LoadedCartT, MyCartAction } from "../types";
import {
  SwarovskiOptikClientCart,
  type AddToCartActionT,
  type SwarovskiOptikClientCartLineItem,
} from "@swo/storefront";
import { ecommerceTrackingAspect } from "../../google-tag-manager/ecommerce-tracking-aspect";
import { pipe } from "fp-ts/lib/function";
import { CartMutationClient } from "../actions/cart-mutation-client";
import { Either } from "fp-ts/lib/Either";
import { either } from "fp-ts";
import { useStore } from "../../../store/hooks/use-store";
import { useExecutor } from "./use-executor";
import { useOpaqueValue } from "../../../hooks/use-opaque-value";
import { useTabBackInFocus } from "../../../hooks/use-tab-visibility";
import { useResourceRelatedInfo2 } from "../../../hooks/use-resource-related-info-2";
import { LoadingStateWithIdle, LoadingStateWithIdleOps } from "../../data-layer/loading-state-with-idle";
import { LoadingStateOps } from "../../data-layer/loading-state";
import { getCart, GetCartResponseT } from "../get-cart";
import { useStaleWhileRevalidate } from "./useStaleWhileRevalidate";
import { useRenderKey } from "../../../hooks/use-render-key";
import { LocaleSegmentContext } from "../../../global/page-context";
import { useUnauthorisedPanicMode } from "./useUnauthorisedPanicMode";

export type CartError = "shopping-not-possible" | "shopping-currently-not-possible";

export function useCart(
  localeSegmentCtx: LocaleSegmentContext,
  recoverCartId: string | null,
): LoadingStateWithIdle<
  Either<"shopping-not-possible" | "shopping-currently-not-possible" | "unauthorised", LoadedCartT>
> {
  const {
    state: { auth },
  } = useStore();

  const token = auth?.authToken.access_token;

  const tokenWithFallback = token || "";
  const [fetchKey, nextFetchKey] = useRenderKey(tokenWithFallback);

  const getVersionBits = (data: { token: string; key: string }) => data.token + data.key;

  const fetchResource = token ? LoadingStateWithIdleOps.asLoaded({ token, key: fetchKey }) : LoadingStateOps.loadingS;
  const { state: _fetchedCart, settleExternally } = useResourceRelatedInfo2(
    fetchResource,
    getVersionBits,
    _ => either.right(_.token),
    _ => getCart(_, localeSegmentCtx.locale.code, localeSegmentCtx.segment.key, undefined, recoverCartId),
  );

  useUnauthorisedPanicMode(_fetchedCart, token);

  const isFetching = _fetchedCart.__tag === "loading";

  const fetchedCart = useStaleWhileRevalidate(_fetchedCart);

  const tokenRef = useOpaqueValue(token);
  useTabBackInFocus(() => {
    if (tokenRef.current) {
      nextFetchKey();
    }
  }, []);

  const [updatingKey, executor] = useExecutor<MyCartAction["tag"]>();

  if (!token) {
    return LoadingStateWithIdleOps.loadingS;
  }

  const cartClient = new CartMutationClient(localeSegmentCtx.locale, localeSegmentCtx.segment, token);

  function extractCart(v: LoadingStateWithIdle<{ result: GetCartResponseT }>): SwarovskiOptikClientCart | null {
    return v.__tag === "loaded" && v.data.result._tag === "Right" ? v.data.result.right : null;
  }

  const getFetchedCart = () => {
    const cart = extractCart(fetchedCart);

    if (cart) {
      return cart;
    } else {
      return null;
    }
  };

  const dispatchAction = (action: MyCartAction): Promise<void> => {
    const versionBits = getVersionBits({ token: tokenWithFallback, key: fetchKey });
    const fetchedCart = getFetchedCart();

    switch (action.tag) {
      case "add-items-to-cart": {
        return executor(action.tag, {}, () => cartClient.addToCart(fetchedCart, action.items)).then(
          ({ cart, knownErrors }) => {
            if (cart) {
              settleExternally({
                result: either.right(cart),
                version: versionBits,
              });

              function isJustAddedLineItem(
                lineItem: SwarovskiOptikClientCartLineItem,
                addedItems: AddToCartActionT["items"],
              ): boolean {
                return addedItems.some(addedItem => {
                  switch (addedItem.tag) {
                    case "standard": {
                      return (
                        lineItem.data.tag === "standard" &&
                        lineItem.data.variant.id === addedItem.variantId &&
                        lineItem.data.productId === addedItem.productId
                      );
                    }
                    case "gift-card": {
                      return (
                        lineItem.data.tag === "gift-card" &&
                        lineItem.data.variant.id === addedItem.variantId &&
                        lineItem.data.productId === addedItem.productId
                      );
                    }
                    case "bundle": {
                      return (
                        lineItem.data.tag === "bundle" &&
                        lineItem.data.bundleElements.some(be => addedItem.selectedSkus.includes(be.variant.sku ?? ""))
                      );
                    }
                    default: {
                      const _: never = addedItem;

                      return _;
                    }
                  }
                });
              }

              const addedLineItem = cart.lineItems.filter(lineItem => isJustAddedLineItem(lineItem, action.items));

              ecommerceTrackingAspect.addToCart(addedLineItem, localeSegmentCtx).push();
            }
          },
        );
      }

      case "remove-line-item-from-cart": {
        // This action exits both there and in checkout handler
        // The reason is that header features a cart dropdown that allows not only previewing cart
        // but also removing its line items
        // This element is not visible in checkout page so they should not interfere
        if (!fetchedCart) {
          return Promise.resolve();
        }

        return executor(action.tag, {}, () => cartClient.removeFromCart(fetchedCart, action.lineItems)).then(
          ({ cart, knownErrors }) => {
            if (cart) {
              settleExternally({
                result: either.right(cart),
                version: versionBits,
              });
            }

            if (action.lineItems.length > 0) {
              ecommerceTrackingAspect.removeFromCart(action.lineItems, localeSegmentCtx).push();
            }
          },
        );
      }

      default: {
        const _: never = action;

        return _;
      }
    }
  };

  return LoadingStateWithIdleOps.mapI(fetchedCart, ({ result, version }) => {
    const publicSettleExternally = (cart: SwarovskiOptikClientCart) =>
      settleExternally({
        result: either.right(cart),
        version: getVersionBits({ token: token, key: fetchKey }),
      });

    return pipe(
      result,
      either.map(c => ({
        cart: c,
        versionKey: version,
        updatingKey: updatingKey?.key || null,
        dispatchAction,
        updateFetchedCart: publicSettleExternally,
        requestCartRefetch: () => nextFetchKey(),
        isFetching,
      })),
    );
  });
}
